import { FormGroup, FormControl } from '@angular/forms';
import {
  Component,
  OnInit,
  Inject,
  Input,
  OnDestroy,
  signal,
  inject} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  firstValueFrom,
  forkJoin,
  of,
  Subscription,
  throwError} from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import {
  IWHEventLogDTO,
  IWHFlowContactDTO,
  IWHProjectWorkObjectDTO,
  WHTranslationTypeENUM,
  WHSafeTranslateService,
  WHToastService,
} from '@workheld/workheld-shared-lib';

// SERVICES
import {
  FLOW_GATEWAY,
  WHIconENUM,
  WHEventLogDataService,
  WHEventLogTypeENUM,
  WHEventLogSourceENUM,
  WHWorkObjectDataService,
  IWHEventLogEndpointConfigModel,
  IWHWorkObjectEndpointConfigModel} from '@workheld/workheld-shared-lib';
import { environment } from 'src/environments/environment';
import { DataModelHelperService } from 'src/app/app-services-helper/data-model-helper.service';
import { DateTimeHelperService } from 'src/app/app-services-helper/date-time-helper.service';
import { WorkOrderAsyncService } from 'src/app/app-services-async/work-order-async.service';
import { FormReferenceService } from 'src/app/app-services-helper/form-reference.service';
import moment from 'moment';
import { WHEnumDataService } from 'src/app/app-services-helper/w-h-enum-helper.service';
import { isFuture } from 'date-fns';
import { RemindTechnicianService } from 'src/app/app-services/remind-technician.service';
import {
  notificationTimeFunction,
  updateAlertOptions} from 'src/app/app-shared/functions/dialogs/remind-technician';
import { dynamicAnimation } from 'src/app/app-shared/helpers/animations';
import { UIService } from 'src/app/app-services/ui.service';

@Component({
  selector: 'ng-bee-mat-dialog-contact-customer-entry',
  templateUrl: './mat-dialog-contact-customer-entry.component.html',
  styleUrls: ['./mat-dialog-contact-customer-entry.component.scss'],
  animations: [dynamicAnimation],
  standalone: false})
export class MatDialogContactCustomerEntryComponent
  implements OnInit, OnDestroy
{
  @Input() eventLogEndpointConfig: IWHEventLogEndpointConfigModel = {
    apiUrl: environment.apiUrl + FLOW_GATEWAY};
  // FORM
  public contactCustomerFormGroup: FormGroup;

  public workObjectTypeId: number;
  public workObjectId: string;
  public contacts: IWHFlowContactDTO[];

  // STATE
  public saving: boolean = false;

  // MANAGE SUBSCRIPTIONS
  public subscriptions: Subscription[] = [];
  ENUMS = WHTranslationTypeENUM;
  private remindTechnicianService = inject(RemindTechnicianService);
  alertOptions = this.remindTechnicianService.alertOptions();

  // DATE VALUES
  public tempFormGroup = new FormGroup({
    dateSlotForm: new FormControl(),
    timeSlotForm: new FormControl()});
  $ENUMS = signal(null);
  workOrders = [];
  isWithoutWorksteps = signal(false);
  notificationTimeInfo = signal('');
  currentLocale = signal('en');

  constructor(
    private eventLogDataService: WHEventLogDataService,
    private workObjectDataService: WHWorkObjectDataService,
    private dataModelHelperService: DataModelHelperService,
    private dateTimeHelperService: DateTimeHelperService,
    private workOrderAsyncService: WorkOrderAsyncService,
    private safeTranslateService: WHSafeTranslateService,
    public ngxToastrService: WHToastService,
    public matDialogRef: MatDialogRef<MatDialogContactCustomerEntryComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      workObjectTypeId: number;
      workObjectId: string;
      contacts: IWHFlowContactDTO[];
    },
    private formReferenceService: FormReferenceService,
    private whEnumDataService: WHEnumDataService,
    private uiService: UIService,
  ) {}

  async ngOnInit() {
    this.currentLocale = this.uiService.$currentLocale;
    this.$ENUMS = this.whEnumDataService.$ENUMS;

    this.contactCustomerFormGroup =
      this.eventLogDataService.initContactCustomerFromGroup();

    // add new value reminderOffsetInMinutes
    this.contactCustomerFormGroup.addControl(
      'reminderOffsetInMinutes',
      new FormControl(null),
    );

    this.subscriptions.push(
      this.contactCustomerFormGroup
        .get('reminderOffsetInMinutes')
        ?.valueChanges.pipe(debounceTime(500))
        .subscribe((value) => {
          this.notificationTimeInfo.set(
            notificationTimeFunction(
              this.contactCustomerFormGroup.controls['reminderOffsetInMinutes']
                ?.value,
              this.tempFormGroup.controls['dateSlotForm']?.value,
              this.tempFormGroup.controls['timeSlotForm']?.value,
            ),
          );
        }),
    );

    this.workObjectTypeId = this.data.workObjectTypeId;
    this.workObjectId = this.data.workObjectId;
    this.contacts = this.data.contacts;

    this.workOrders = await firstValueFrom(
      this.workOrderAsyncService.getWorkOrderDtoList(
        this.workObjectTypeId,
        this.workObjectId,
      ),
    );

    this.isWithoutWorksteps.set(
      this.workOrders.every((w) => w.workStepCount === 0),
    );

    if (this.isContactsEmpty) {
      this.contactCustomerFormGroup.disable();
    } else {
      this.contactCustomerFormGroup.controls[
        'enumReason'
      ].valueChanges.subscribe((val) => {
        if (val === 'NO')
          //Clear time-slot control value to make it valid in case of
          // user selected that they did not contacted the customer after
          // previous set of time-slot to invalid value - so they are able to save the form.
          this.contactCustomerFormGroup.controls['timeSlot'].patchValue(null);
      });
    }

    this.subscriptions.push(
      this.tempFormGroup.controls['dateSlotForm'].valueChanges.subscribe(
        (value: string) => {
          const time = this.tempFormGroup.controls['timeSlotForm'].value;
          const finalVersion = time
            ? moment(value)
                .set({ hour: time.slice(0, 2), minute: time.slice(-2) })
                .format()
            : moment(value).format();
          this.contactCustomerFormGroup.controls['timeSlot'].patchValue(
            finalVersion,
          );
          this.alertOptions = updateAlertOptions(
            this.remindTechnicianService.alertOptions(),
            this.tempFormGroup.controls['dateSlotForm']?.value,
            this.tempFormGroup.controls['timeSlotForm']?.value,
          );
        },
      ),
    );

    this.subscriptions.push(
      this.tempFormGroup.controls['dateSlotForm'].valueChanges.subscribe(
        (value: string) => {
          if (value && !this.tempFormGroup.controls['timeSlotForm'].value) {
            this.tempFormGroup.controls['timeSlotForm'].patchValue('00:00');
          }
        },
      ),
    );

    this.subscriptions.push(
      this.tempFormGroup.valueChanges.subscribe((value: any) => {
        const date = this.tempFormGroup.controls['dateSlotForm'].value;
        const time = this.tempFormGroup.controls['timeSlotForm'].value;
        let finalVersion = null;
        const m = date
          ? moment(date).set({
              hour: time?.slice(0, 2),
              minute: time?.slice(-2)})
          : moment(date);

        if (m.isValid()) {
          finalVersion = m.format();
        }

        this.contactCustomerFormGroup.controls['timeSlot'].patchValue(
          finalVersion,
        );

        if (
          finalVersion &&
          isFuture(finalVersion) &&
          this.workOrders.length > 0
        ) {
          this.contactCustomerFormGroup.controls[
            'reminderOffsetInMinutes'
          ].enable();
        } else {
          this.contactCustomerFormGroup.controls[
            'reminderOffsetInMinutes'
          ].disable();
          this.contactCustomerFormGroup.controls[
            'reminderOffsetInMinutes'
          ].reset();
        }

        if (!time) {
          this.contactCustomerFormGroup.controls[
            'reminderOffsetInMinutes'
          ].reset();
        }

        this.alertOptions = updateAlertOptions(
          this.remindTechnicianService.alertOptions(),
          this.tempFormGroup.controls['dateSlotForm']?.value,
          this.tempFormGroup.controls['timeSlotForm']?.value,
        );
      }),
    );
  }

  public async createLogAndUpdateWorkObject() {
    if (this.isFormGroupInvalid) return;

    const templatePayload: any = this.contactCustomerFormGroup.value;
    const workObjectPayload = {
      id: this.workObjectId,
      scheduledStartDate: null,
      scheduledEndDate: null,
      hardReschedule: true};

    const workObjectEndpointConfig: IWHWorkObjectEndpointConfigModel = {
      apiUrl: environment.apiUrl + FLOW_GATEWAY,
      workObjectTypeId: this.workObjectTypeId,
      workObjectId: this.workObjectId};

    const payload: IWHEventLogDTO = {
      comment:
        this.safeTranslateService.instant(
          this.$ENUMS().CUSTOMER_CONTACT.filter(
            (r) => r.id == templatePayload.enumReason,
          )[0]?.translateKey,
        ) +
        (templatePayload.timeSlot
          ? ' on ' + moment(templatePayload.timeSlot).format('DD.MM.YYYY HH:mm')
          : '') +
        (templatePayload.comment ? ', ' + templatePayload.comment : ''),
      eventLogType:
        this.enumReason === 'NO'
          ? WHEventLogTypeENUM.CUSTOMER_CALL
          : WHEventLogTypeENUM.APPOINTMENT_SET,
      workObjectId: this.workObjectId,
      eventLogSource: WHEventLogSourceENUM.USER,
      createdAt: null,
      sourceLoginDetailURL: null,
      sourceLoginId: null,
      workObjectTitle: null};

    this.saving = true;

    this.eventLogDataService
      .createEventLog(this.eventLogEndpointConfig, payload)
      .pipe(
        switchMap(() => {
          if (
            this.contactCustomerFormGroup.controls['enumReason'].value == 'YES'
          ) {
            const scheduledStartDate =
              this.dateTimeHelperService.dateToIsoStringOffset(
                moment(templatePayload.timeSlot).toDate(),
              );
            const scheduledEndDate =
              this.dateTimeHelperService.dateToIsoStringOffset(
                moment(templatePayload.timeSlot).add(2, 'hours').toDate(),
              );

            workObjectPayload.scheduledStartDate = scheduledStartDate;
            workObjectPayload.scheduledEndDate = scheduledEndDate;

            return this.workObjectDataService.rescheduleWorkObject(
              workObjectEndpointConfig,
              workObjectPayload,
            );
          }
          return of([]);
        }),
        switchMap((workObject) => {
          // SETTING TECHNICIAN NOTIFICATIONS
          if (workObject?.id) {
            const workOrderPayload: any = {
              reminderOffsetInMinutes: templatePayload?.reminderOffsetInMinutes,
              reminderComment: templatePayload?.comment};

            const requests = this.workOrders
              .filter((wo) => wo.id !== null)
              .map((workOrder) =>
                this.workOrderAsyncService
                  .updateWorkOrder(workOrder.id, workOrderPayload)
                  .pipe(
                    map(() => ({ success: true, title: workOrder.title })),
                    catchError(() =>
                      of({ success: false, title: workOrder.title }),
                    ),
                  ),
              );

            return forkJoin(requests).pipe(
              tap((results) => {
                const successfulTitles = results
                  .filter((r) => r.success)
                  .map((r) => r.title);
                const failedTitles = results
                  .filter((r) => !r.success)
                  .map((r) => r.title);

                if (successfulTitles.length) {
                  this.ngxToastrService.success('technicianNotification.toast.creationSuccess', { params: {
                      workOrderTitle: successfulTitles.join(', ')} });
                }

                if (failedTitles.length) {
                  this.ngxToastrService.info('technicianNotification.toast.creationFailed', { params: {
                      workOrderTitle: failedTitles.join(', ')} });
                }
              }),
            );
          }

          return of(null);
        }),
        switchMap(() => {
          const payload: IWHProjectWorkObjectDTO =
            this.workObjectDataService.initWorkObjectCustomerVisitPlannedPayload(
              this.workObjectId,
            );
          return this.workObjectDataService.updateWorkObjectByWorkObjectType(
            workObjectEndpointConfig,
            {
              ...payload,
              startDate: workObjectPayload.scheduledStartDate,
              endDate: workObjectPayload.scheduledEndDate} as any,
          );
        }),
        catchError((err) => {
          this.saving = false;
          return throwError(err);
        }),
      )
      .subscribe((resp: any) => {
        this.saving = false;
        const workObjectDOM =
          this.dataModelHelperService.initWorkObjectTypeDOM(resp);

        this.formReferenceService.unsavedChanges = false;
        this.saving = false;
        this.matDialogRef.close({ confirm: true, dom: workObjectDOM });
      });
  }

  cancel() {
    if (this.contactCustomerFormGroup.dirty) {
      const ref = this.formReferenceService.createDialog();
      this.subscriptions.push(
        ref
          .pipe(
            tap((canDiscard) => {
              if (canDiscard) {
                this.matDialogRef.close();
              }
            }),
          )
          .subscribe(),
      );
    } else {
      this.matDialogRef.close(false);
    }
  }

  public get isFormGroupInvalid(): boolean {
    return (
      this.contactCustomerFormGroup.invalid ||
      this.contacts === undefined ||
      this.contacts.length <= 0 ||
      this.isTimeSlotInvalid
    );
  }

  public get isTimeSlotInvalid(): boolean {
    return (
      this.contactCustomerFormGroup.controls['enumReason'].value == 'YES' &&
      this.contactCustomerFormGroup.controls['timeSlot'].value == null
    );
  }

  public get saveIcon(): string {
    return WHIconENUM.SaveIcon as string;
  }

  public get cancelIcon(): string {
    return WHIconENUM.CancelIcon as string;
  }

  public get contacPersonIcon(): string {
    return WHIconENUM.UserIcon as string;
  }

  public get userIcon() {
    return WHIconENUM.UserIcon;
  }

  public get isContactsEmpty(): boolean {
    return this.contacts === undefined || this.contacts.length <= 0;
  }

  public get timeSlotFormControl(): FormControl {
    return this.contactCustomerFormGroup.controls['timeSlot'] as FormControl;
  }

  public get dateSlotForm(): string {
    return this.tempFormGroup.controls['dateSlotForm'].value as string;
  }

  public get timeSlotForm(): string {
    return this.tempFormGroup.controls['timeSlotForm'].value as string;
  }

  public get enumReason(): string {
    return this.contactCustomerFormGroup.controls['enumReason'].value as string;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
}
